import {
  DESKTOP_VIEWPORT_FALLBACK,
  VIEWPORT_TOKENS,
  type LayoutMode,
  type PointerMode,
  type ViewportCapabilities,
} from './tokens'

const LAYOUT_QUERY_PARAM = 'layout'
const LAYOUT_STORAGE_KEY = 'f2h-layout-mode'

function readPointerMode(): PointerMode {
  if (typeof window === 'undefined') return 'fine'
  if (window.matchMedia('(pointer: coarse)').matches) return 'coarse'
  if (window.matchMedia('(hover: none)').matches) return 'coarse'
  return 'fine'
}

function readLayoutOverride(): LayoutMode | null {
  if (typeof window === 'undefined') return null

  const fromQuery = new URLSearchParams(window.location.search).get(LAYOUT_QUERY_PARAM)
  if (fromQuery === 'compact' || fromQuery === 'desktop') return fromQuery

  try {
    const stored = window.localStorage.getItem(LAYOUT_STORAGE_KEY)
    if (stored === 'desktop') return stored
    if (stored === 'compact') {
      // A leftover phone/tablet flag must not pin CompactChrome on a mouse desktop.
      const pointerMode = readPointerMode()
      if (pointerMode === 'fine' && window.innerWidth >= VIEWPORT_TOKENS.compactMaxWidthPx) {
        return null
      }
      return stored
    }
  } catch {
    // private mode / blocked storage
  }

  return null
}

function readSafeArea(): ViewportCapabilities['safeArea'] {
  if (typeof window === 'undefined') return DESKTOP_VIEWPORT_FALLBACK.safeArea

  const styles = window.getComputedStyle(document.documentElement)
  const readInset = (name: string) => {
    const raw = styles.getPropertyValue(name).trim()
    const parsed = Number.parseFloat(raw)
    return Number.isFinite(parsed) ? parsed : 0
  }

  return {
    top: readInset('--safe-area-inset-top') || readInset('env(safe-area-inset-top)'),
    bottom: readInset('--safe-area-inset-bottom') || readInset('env(safe-area-inset-bottom)'),
    left: readInset('--safe-area-inset-left') || readInset('env(safe-area-inset-left)'),
    right: readInset('--safe-area-inset-right') || readInset('env(safe-area-inset-right)'),
  }
}

export function readViewportCapabilities(): ViewportCapabilities {
  if (typeof window === 'undefined') return DESKTOP_VIEWPORT_FALLBACK

  const width = window.innerWidth
  const height = window.innerHeight
  const override = readLayoutOverride()
  const pointerMode = readPointerMode()
  const compactByWidth = width < VIEWPORT_TOKENS.compactMaxWidthPx
  const compactByTablet =
    pointerMode === 'coarse' && width < VIEWPORT_TOKENS.tabletCompactMaxWidthPx

  const layoutMode: LayoutMode = override ?? (compactByWidth || compactByTablet ? 'compact' : 'desktop')
  const isCompactTablet =
    layoutMode === 'compact' &&
    Math.min(width, height) >= VIEWPORT_TOKENS.compactTabletMinShortSidePx

  return {
    layoutMode,
    pointerMode,
    orientation: height >= width ? 'portrait' : 'landscape',
    width,
    height,
    isCompactTablet,
    safeArea: readSafeArea(),
  }
}

/** Cotas 2D/3D: solo se agrandan en tablet; el móvil conserva el tamaño original. */
export function scaleCotaFont(size: number) {
  const { isCompactTablet } = readViewportCapabilities()
  if (isCompactTablet) return Math.round(size * 1.55)
  return size
}

/**
 * Stroke of a cota line in world mm. Desktop 7mm ≈ 1px at zoom 0.15.
 * Compact needs a little more so the line is not <1px, but never a fat bar.
 */
export function scaleCotaStroke(worldMm: number) {
  const { layoutMode, width } = readViewportCapabilities()
  if (layoutMode !== 'compact') return worldMm
  const canvasW = width > 0 ? width : 390
  const approxZoom = Math.min(Math.max(canvasW / 7200, 0.05), 0.12)
  const minMm = 1.05 / approxZoom
  return Math.min(Math.max(worldMm, minMm), 14)
}

/** Phone/tablet with touch. Never a mouse desktop, even if the window is narrow. */
export function isLightweightViewer(): boolean {
  if (typeof window === 'undefined') return false
  const { layoutMode, pointerMode } = readViewportCapabilities()
  return layoutMode === 'compact' && pointerMode === 'coarse'
}

/** Compact / small viewport: keep manual save, skip the timed autosave. */
export function shouldDisableAutoSave(): boolean {
  if (typeof window === 'undefined') return false
  const { layoutMode, width, pointerMode } = readViewportCapabilities()
  return (
    layoutMode === 'compact' ||
    width < VIEWPORT_TOKENS.compactMaxWidthPx ||
    (pointerMode === 'coarse' && width < VIEWPORT_TOKENS.tabletCompactMaxWidthPx)
  )
}

/** Concurrent GLB parses / Draco workers. Phone=1, tablet/desktop=2. */
export function getGlbParseConcurrency(): number {
  if (typeof window === 'undefined') return 1
  const { layoutMode, pointerMode, isCompactTablet } = readViewportCapabilities()
  if (layoutMode === 'compact' && pointerMode === 'coarse') {
    return isCompactTablet ? 2 : 1
  }
  return 2
}

export function getDracoWorkerLimit(): number {
  return getGlbParseConcurrency()
}

/** Unique furniture catalogs to parse before painting the next layer. */
export function getProjectFurnitureBatchSize(): number {
  if (!isLightweightViewer()) return 8
  const { isCompactTablet } = readViewportCapabilities()
  return isCompactTablet ? 6 : 4
}

/** Let Safari paint between heavy GLB parses so the tab is not killed. */
export function yieldToMainThread(): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(() => requestAnimationFrame(() => resolve()), 0)
  })
}
